import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useCallback } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { fetchAllProjectsWithPairs } from '@/entities/projects-data/api/fetchAllProjectsWithPairs';
import { syncProjectsToRedux } from '@/entities/projects-data/lib/syncToRedux';
import { PROJECTS_DATA_QUERY_KEYS } from '@/entities/projects-data/model/projectsDataQueryKeys';

import { addSolanaPair } from 'entities/project/api/project/api';
import { useTypedDispatch } from 'store';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { InputField } from 'fields/base/InputField/InputField';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { Modal } from 'ui/Modal/Modal';

import {
  initialSolanaPairFormValues,
  toAddSolanaPairRequest,
  validateSolanaPairForm,
  type SolanaPairFormValues,
} from './model/form';

type AddSolanaPairModalProps = {
  projectId: number;
  onClose: () => void;
};

export const AddSolanaPairModal = ({ projectId, onClose }: AddSolanaPairModalProps) => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const refreshProjects = useCallback(async () => {
    try {
      const projectsData = await fetchAllProjectsWithPairs();
      syncProjectsToRedux(dispatch, projectsData);
      queryClient.setQueryData(PROJECTS_DATA_QUERY_KEYS.list(), projectsData, {
        updatedAt: Date.now(),
      });
      await queryClient.invalidateQueries({ queryKey: PROJECTS_DATA_QUERY_KEYS.all });
    } catch (error) {
      console.error('[AddSolanaPairModal] Failed to refresh projects', error);
    }
  }, [dispatch, queryClient]);

  const submit = useCallback(
    async (values: SolanaPairFormValues) => {
      const response = await addSolanaPair(toAddSolanaPairRequest(projectId, values));
      if (!response.isSuccess) {
        toast.error(response.errorMessage || 'Failed to create Raydium AMM v4 pair');
        return;
      }

      onClose();
      toast.success(
        `Created Raydium AMM v4 pair: ${values.baseSymbol.trim()}/${values.quoteSymbol.trim()}`,
      );
      void refreshProjects();
      navigate(
        generatePath(ROUTE_PATHS.solPair, {
          projectId: projectId.toString(),
          pairId: response.data.id.toString(),
        }),
      );
    },
    [navigate, onClose, projectId, refreshProjects],
  );

  const { errors, handleBlur, handleSubmit, isSubmitting, setFieldValue, touched, values } =
    useFormik({
      initialValues: initialSolanaPairFormValues,
      onSubmit: submit,
      validate: validateSolanaPairForm,
    });

  const setValue = useCallback(
    (field: keyof SolanaPairFormValues, value: string) => {
      void setFieldValue(field, value);
    },
    [setFieldValue],
  );

  const error = useCallback(
    (field: keyof SolanaPairFormValues) => (touched[field] ? errors[field] : undefined),
    [errors, touched],
  );

  return (
    <Modal
      title="New Raydium AMM v4 pair"
      onClose={onClose}
      className="mm-add-solana-pair-modal"
      customButtons={
        isSubmitting ? (
          <ButtonLoading />
        ) : (
          <Button type="button" onClick={() => handleSubmit()}>
            Create
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-5">
        <p className="text-sm text-secondary">
          Solana · Raydium AMM v4. The manager verifies the pool, vaults and mint details before
          saving. This action does not create wallets or start bots.
        </p>
        <InputField
          name="poolAddress"
          label="Raydium pool address"
          type="text"
          value={values.poolAddress}
          setValue={value => setValue('poolAddress', value)}
          onBlur={handleBlur}
          errorMessage={error('poolAddress')}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <InputField
            name="baseMint"
            label="Base mint"
            type="text"
            value={values.baseMint}
            setValue={value => setValue('baseMint', value)}
            onBlur={handleBlur}
            errorMessage={error('baseMint')}
          />
          <InputField
            name="baseSymbol"
            label="Base symbol"
            type="text"
            value={values.baseSymbol}
            setValue={value => setValue('baseSymbol', value)}
            onBlur={handleBlur}
            errorMessage={error('baseSymbol')}
          />
          <InputField
            name="baseDecimals"
            label="Base decimals"
            type="natural-number"
            value={values.baseDecimals}
            setValue={value => setValue('baseDecimals', value)}
            onBlur={handleBlur}
            errorMessage={error('baseDecimals')}
          />
          <InputField
            name="quoteMint"
            label="Quote mint"
            type="text"
            value={values.quoteMint}
            setValue={value => setValue('quoteMint', value)}
            onBlur={handleBlur}
            errorMessage={error('quoteMint')}
          />
          <InputField
            name="quoteSymbol"
            label="Quote symbol"
            type="text"
            value={values.quoteSymbol}
            setValue={value => setValue('quoteSymbol', value)}
            onBlur={handleBlur}
            errorMessage={error('quoteSymbol')}
          />
          <InputField
            name="quoteDecimals"
            label="Quote decimals"
            type="natural-number"
            value={values.quoteDecimals}
            setValue={value => setValue('quoteDecimals', value)}
            onBlur={handleBlur}
            errorMessage={error('quoteDecimals')}
          />
        </div>
        <InputField
          name="notes"
          label="Notes (optional)"
          type="text"
          value={values.notes}
          setValue={value => setValue('notes', value)}
          onBlur={handleBlur}
          errorMessage={error('notes')}
        />
      </div>
    </Modal>
  );
};
